import { forwardRef, type HTMLAttributes } from 'react';
import { AlertCircle, CheckCircle, AlertTriangle, Info, Brain, X } from 'lucide-react';
import { cn, alertVariants, type AlertVariants } from '../../../utils';

const iconMap = {
  default: null,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
  purple: Brain,
};

export interface AlertProps extends HTMLAttributes<HTMLDivElement>, AlertVariants {
  onDismiss?: () => void;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = 'default', children, onDismiss, ...props }, ref) => {
    const Icon = iconMap[variant || 'default'];
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {Icon && <Icon className="h-5 w-5" />}
        <div className="flex-1 min-w-0">{children}</div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="shrink-0 p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
            aria-label="Dismiss alert"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);
Alert.displayName = 'Alert';

export interface AlertTitleProps extends HTMLAttributes<HTMLHeadingElement> {}

export const AlertTitle = forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, children, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn('font-medium leading-none tracking-tight', className)}
      {...props}
    >
      {children}
    </h5>
  )
);
AlertTitle.displayName = 'AlertTitle';

export interface AlertDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

export const AlertDescription = forwardRef<HTMLParagraphElement, AlertDescriptionProps>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn('text-sm [&_p]:leading-relaxed', className)}
      {...props}
    />
  )
);
AlertDescription.displayName = 'AlertDescription';
